import React, { FC } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { THEME_ALU, THEME_PRIMARY, THEME_RED, THEME_SMOKY, themeColor } from '../../../../styles/basics/colors';
import i18n from 'i18next';

export const LC_STATUS_LABEL_NEW_UPDATED = i18n.t('yourLocalContent.yourTitles.newUpdated');
export const LC_STATUS_LABEL_PROCESSING = i18n.t('yourLocalContent.yourTitles.processing');
export const LC_STATUS_LABEL_LIVE_IN_COLLECTION = i18n.t('yourLocalContent.yourTitles.liveInCollection');
export const LC_STATUS_LABEL_LIVE_ON_LIBX = i18n.t('yourLocalContent.yourTitles.liveOnLibx');
export const LC_STATUS_LABEL_INACTIVE = i18n.t('yourLocalContent.yourTitles.inactive');

const bulletSize = '1em';

const disabledColor: string = themeColor(THEME_ALU);
const enabledColor: string = themeColor(THEME_PRIMARY);
const libxColor: string = themeColor(THEME_SMOKY);
const inactiveColor: string = themeColor(THEME_RED);

const StepText = styled.div`
    font-size: 15px;
    line-height: 15px;
`;

const Step = styled.li<{ numberOfSteps: number; bold: boolean; forceGreenLine: boolean; color: string }>`
    position: relative;
    float: left;
    width: calc(100% / ${props => props.numberOfSteps});
    text-align: center;
    color: ${props => props.color};
    font-weight: ${props => (props.bold ? 'bold' : 'initial')};

    & .active-step ~ & {
        color: ${props => props.color};
    }

    &:before {
        content: '';
        display: block;
        background-color: ${props => props.color};
        border: 1px solid ${props => props.color};
        text-align: center;
        width: ${bulletSize};
        height: ${bulletSize};
        line-height: ${bulletSize};
        border-radius: ${bulletSize};
        position: relative;
        left: 50%;
        margin-bottom: calc(${bulletSize} / 2);
        margin-left: calc(${bulletSize} * -0.5);
        z-index: 1;

        & .active-step ~ & {
            background-color: ${props => props.color};
            color: ${props => props.color};
            border-color: ${props => props.color};
        }
    }

    & + li {
        &:after {
            content: '';
            display: block;
            width: 100%;
            background-color: ${props => (props.forceGreenLine ? props.color : disabledColor)};
            height: 1px;
            position: absolute;
            left: -50%;
            top: calc(${bulletSize} / 2);
            z-index: 0;
        }
    }

    & .active-step ~ &:after {
        background-color: ${props => (props.forceGreenLine ? props.color : disabledColor)};
    }
`;

interface Props {
    inactive?: boolean;
    forceGreenLine?: boolean;
    text?: string;
    fainted?: boolean;
    numberOfSteps: number;
    highlighted?: boolean;
    enabled?: boolean;
}

const StatusIndicatorStep: FC<Props> = ({
    enabled = false,
    text = '',
    highlighted = false,
    fainted,
    inactive,
    forceGreenLine = false,
    numberOfSteps
}) => {
    function getColorFromProps(enabled: boolean) {
        if (inactive) {
            return inactiveColor;
        } else if (enabled) {
            return enabledColor;
        } else {
            if (fainted) {
                return libxColor;
            }
            return disabledColor;
        }
    }

    const color = getColorFromProps(enabled);
    const classes = classNames({ 'active-step': enabled });

    return (
        <Step
            numberOfSteps={numberOfSteps}
            className={classes}
            color={color}
            bold={highlighted}
            forceGreenLine={forceGreenLine}
            data-testid="status-indicator"
        >
            <StepText>{text}</StepText>
        </Step>
    );
};

export default StatusIndicatorStep;
